import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate, useParams } from "react-router-dom";
import { selectUIStates, selectUser, setUIStates } from "../../../redux/features/appData";
import {
  EmployeeRecord,
  EmployeeReferenceType,
  Workflow,
  WorkflowStatus,
} from "winslow-core";
import { 
  apiGetWorkflow,
  apiGetWorkflowsForWorkspace, 
  apiUpdateWorkflowStatus, 
  apiDeleteWorkflow 
} from "../../../services/WorkflowService";
import { apiGetEmployeeRecordsForWorkspace } from "../../../services/EmployeeRecordService";
import { GenericSelect, GenericSelectOption } from "../../shared/GenericSelect";
import { Icon } from "../../../ds2/components/Icon";
import { Button } from "../../../ds2/components";
import { Eye, Pause, Trash2, Play, RefreshCw, User, X } from "lucide-react";
import toast from "react-hot-toast";
import { toastFail, toastSuccess } from "../../../config";
import "../../../ds2/components/Table.css";
import "./AppWorkflows.css";
import { erGetCPValue } from "../app-employee-records/employeeRecordUtils";
import { AppWorkflow } from "../vibe-coder/AppWorkflow";
import { openEmployeeProfileSidebar } from "../../../redux/features/agent/agentSlice";
import ModalBasicConfirm from "../../common/ModalBasicConfirm";

type AppWorkflowsProps = {};

export const AppWorkflows = ({}: AppWorkflowsProps) => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  
  const storeUser = useSelector(selectUser);
  const uiStates = useSelector(selectUIStates);

  const { employeeRecordId } = useParams();

  const [workflows, setWorkflows] = useState<Workflow[]>([]);
  const [employeeRecords, setEmployeeRecords] = useState<EmployeeRecord[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [selectedStatus, setSelectedStatus] = useState<WorkflowStatus | "ALL">("ALL");
  const [selectedEmployeeRecordId, setSelectedEmployeeRecordId] = useState<string | "ALL">("ALL");
  const [showWorkflowModal, setShowWorkflowModal] = useState<boolean>(false);
  const [selectedWorkflowId, setSelectedWorkflowId] = useState<string | null>(null);
  const [showDeleteConfirmationModal, setShowDeleteConfirmationModal] = useState<boolean>(false);
  const [pendingWorkflowId, setPendingWorkflowId] = useState<string | null>(null);

  useEffect(() => {
    dispatch(setUIStates({...uiStates, hrisLocation: "Workflows"}));
  }, []);

  useEffect(() => {
    if (storeUser?.currentWorkspaceId) {
      loadWorkflows();
      loadEmployeeRecords();
    }
  }, [storeUser?.currentWorkspaceId, employeeRecordId]);

  // Set selected employee record when route has employeeRecordId
  useEffect(() => {
    if (employeeRecordId) {
      setSelectedEmployeeRecordId(employeeRecordId);
    } else {
      setSelectedEmployeeRecordId("ALL");
    }
  }, [employeeRecordId]);

  const loadWorkflows = async () => {
    if (!storeUser?.currentWorkspaceId) return;
    
    setLoading(true);
    try {
      const workflowsData = await apiGetWorkflowsForWorkspace(storeUser.currentWorkspaceId);
      if (!workflowsData) return;

      const filteredWorkflows = employeeRecordId ? workflowsData.filter(wf => wf.creatorEmployeeRecordId === employeeRecordId) : workflowsData;
      // Sort by most recent update (updatedAt) - newest first
      filteredWorkflows.sort((a, b) => new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime());
      
      setWorkflows(filteredWorkflows);
    } catch (error) {
      console.error("Error loading workflows:", error);
      toast("Failed to load workflows", toastFail);
    } finally {
      setLoading(false);
    }
  };

  const loadEmployeeRecords = async () => {
    if (!storeUser?.currentWorkspaceId) return;
    
    try {
      const employeeRecordsData = await apiGetEmployeeRecordsForWorkspace(storeUser.currentWorkspaceId);
      setEmployeeRecords(employeeRecordsData || []);
    } catch (error) {
      console.error("Error loading employee records:", error);
      toast("Failed to load employee records", toastFail);
    }
  };

  const handleViewWorkflow = (workflow: Workflow) => {
    setSelectedWorkflowId(workflow.id);
    setShowWorkflowModal(true);
  };

  const handleViewEmployeeRecord = (employeeRecordId: string) => {
    dispatch(openEmployeeProfileSidebar({
      employeeId: employeeRecordId,
      type: EmployeeReferenceType.EMPLOYEE_RECORD
    }));
  };

  const handlePauseWorkflow = async (workflow: Workflow) => {
    try {
      const updatedWorkflow = await apiUpdateWorkflowStatus(workflow.id, "CANCELLED");
      if (updatedWorkflow) {
        toast("Workflow paused successfully", toastSuccess);
        loadWorkflows(); // Refresh the list
      } else {
        toast("Failed to pause workflow", toastFail);
      }
    } catch (error) {
      console.error("Error pausing workflow:", error);
      toast("Failed to pause workflow", toastFail);
    }
  };

  const handleResumeWorkflow = async (workflow: Workflow) => {
    try {
      const updatedWorkflow = await apiUpdateWorkflowStatus(workflow.id, "PROCESSING");
      if (updatedWorkflow) {
        toast("Workflow resumed successfully", toastSuccess);
        loadWorkflows(); // Refresh the list
      } else {
        toast("Failed to resume workflow", toastFail);
      }
    } catch (error) {
      console.error("Error resuming workflow:", error);
      toast("Failed to resume workflow", toastFail);
    }
  };

  const handleDeleteWorkflow = (workflow: Workflow) => {
    setPendingWorkflowId(workflow.id);
    setShowDeleteConfirmationModal(true);
  };

  const handleConfirmDelete = async () => {
    if (!pendingWorkflowId) {
      return;
    }
    
    try {
      const deletedWorkflow = await apiDeleteWorkflow(pendingWorkflowId);
      if (deletedWorkflow) {
        toast("Workflow deleted successfully", toastSuccess);
        loadWorkflows(); // Refresh the list
      } else {
        toast("Failed to delete workflow", toastFail);
      }
    } catch (error) {
      console.error("Error deleting workflow:", error);
      toast("Failed to delete workflow", toastFail);
    } finally {
      setPendingWorkflowId(null);
      setShowDeleteConfirmationModal(false);
    }
  };

  const getStatusOptions = (): GenericSelectOption[] => {
    return [
      { displayName: "All Workflows", value: "ALL" },
      { displayName: "Processing", value: WorkflowStatus.PROCESSING },
      { displayName: "Waiting", value: WorkflowStatus.WAITING },
      { displayName: "Needs Approval", value: WorkflowStatus.NEEDS_APPROVAL },
      { displayName: "Needs Task", value: WorkflowStatus.NEEDS_TASK },
      { displayName: "Complete", value: WorkflowStatus.COMPLETE },
      { displayName: "Cancelled", value: WorkflowStatus.CANCELLED },
      { displayName: "Failed", value: WorkflowStatus.FAILED },
    ];
  };

  const getStatusBadgeClass = (status: WorkflowStatus): string => {
    switch (status) {
      case WorkflowStatus.PROCESSING:
        return "app-workflows-status-badge app-workflows-status-processing";
      case WorkflowStatus.WAITING:
        return "app-workflows-status-badge app-workflows-status-waiting";
      case WorkflowStatus.NEEDS_APPROVAL:
        return "app-workflows-status-badge app-workflows-status-needs-approval";
      case WorkflowStatus.NEEDS_TASK:
        return "app-workflows-status-badge app-workflows-status-needs-task";
      case WorkflowStatus.COMPLETE:
        return "app-workflows-status-badge app-workflows-status-complete";
      case WorkflowStatus.CANCELLED:
        return "app-workflows-status-badge app-workflows-status-cancelled";
      case WorkflowStatus.FAILED:
        return "app-workflows-status-badge app-workflows-status-failed";
      default:
        return "app-workflows-status-badge";
    }
  };

  const formatDate = (date: Date): string => {
    return new Date(date).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getEmployeeAvatar = (employeeRecordId: string) => {
    const employeeRecord = employeeRecords.find(er => er.id === employeeRecordId);
    return employeeRecord?.avatarURL || null;
  };

  const getEmployeeName = (employeeRecordId: string) => {
    const employeeRecord = employeeRecords.find(er => er.id === employeeRecordId);
    if (!employeeRecord) return "";
    return erGetCPValue(employeeRecord, "system-firstName") + " " + erGetCPValue(employeeRecord, "system-lastName");
  };

  const getEmployeeOptions = (): GenericSelectOption[] => {
    return [
      { displayName: "All Employees", value: "ALL" },
      ...employeeRecords.map((er) => ({
        displayName: `${erGetCPValue(er, "system-firstName")} ${erGetCPValue(er, "system-lastName")}`,
        value: er.id,
        image: er.avatarURL || undefined,
      })),
    ];
  };

  const filteredWorkflows = workflows.filter((workflow) => {
    // Filter by status
    const matchesStatus = selectedStatus === "ALL" || workflow.status === selectedStatus;
    
    // Filter by employee record
    const matchesEmployee = selectedEmployeeRecordId === "ALL" || 
                           workflow.creatorEmployeeRecordId === selectedEmployeeRecordId;
    
    return matchesStatus && matchesEmployee;
  });

  console.log("[AppWorkflows] workflows: ", workflows);
  console.log("[AppWorkflows] filteredWorkflows: ", filteredWorkflows);

  return (
    <div className="app-workflows">
      {/* Header */}
      <div className="app-workflows-header">
        <div className="app-workflows-header-left">
          <h1 className="app-workflows-title">Workflows</h1>
          {/* <p className="app-workflows-subtitle">
            {filteredWorkflows.length} workflow{filteredWorkflows.length !== 1 ? 's' : ''} in your workspace
          </p> */}
        </div>
        <div className="app-workflows-header-right">
          {!employeeRecordId && (
            <div className="app-workflows-filter">
              <label>Employee:</label>
              <GenericSelect
                selected={getEmployeeOptions().find(option => option.value === selectedEmployeeRecordId)}
                options={getEmployeeOptions()}
                onChange={(value) => setSelectedEmployeeRecordId(value as string)}
                maxHeight="300px"
                circleImage={true}
              />
            </div>
          )}
          <div className="app-workflows-filter">
            <label>Status:</label>
            <GenericSelect
              selected={getStatusOptions().find(option => option.value === selectedStatus)}
              options={getStatusOptions()}
              onChange={(value) => setSelectedStatus(value)}
            />
          </div>
          <Button
            variant="secondary"
            size="md"
            leftIcon={<RefreshCw size={16} className={loading ? "app-workflows-refresh-spinning" : ""} />}
            label="Refresh"
            onClick={loadWorkflows}
            disabled={loading}
          />
        </div>
      </div>

      {/* Workflows Table */}
      <div className="ds2-table-container">
        <table className="ds2-table">
          <thead>
            <tr>
              <th>Creator</th>
              <th>Vibe Process</th>
              <th>Status</th>
              <th>Created</th>
              <th>Updated</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr>
                <td colSpan={6} className="app-workflows-loading">
                  <div className="app-workflows-loading-spinner"></div>
                  Loading workflows...
                </td>
              </tr>
            ) : filteredWorkflows.length === 0 ? (
              <tr>
                <td colSpan={6} className="app-workflows-empty">
                  No workflows found
                </td>
              </tr>
            ) : (
              filteredWorkflows.map((workflow) => (
                <tr key={workflow.id} className="app-workflows-row">
                  <td className="app-workflows-creator-cell">
                    <div 
                      className="app-workflows-creator-content app-workflows-creator-clickable"
                      onClick={() => handleViewEmployeeRecord(workflow.creatorEmployeeRecordId)}
                    >
                      {getEmployeeAvatar(workflow.creatorEmployeeRecordId) ? (
                        <img 
                          src={getEmployeeAvatar(workflow.creatorEmployeeRecordId)!} 
                          alt={getEmployeeName(workflow.creatorEmployeeRecordId)}
                          className="app-workflows-creator-avatar"
                        />
                      ) : (
                        <User size={16} className="app-workflows-creator-icon" />
                      )}
                      <span className="app-workflows-creator-name">
                        {getEmployeeName(workflow.creatorEmployeeRecordId)}
                      </span>
                    </div>
                  </td>
                  <td className="app-workflows-vibe-process-cell">
                    <div className="app-workflows-vibe-process-content">
                      <strong>{workflow.vibeProcess?.name || 'Unknown Process'}</strong>
                      {workflow.vibeProcess?.description && (
                        <></>
                        // <p className="app-workflows-vibe-process-description">
                        //   {workflow.vibeProcess.description}
                        // </p>
                      )}
                    </div>
                  </td>
                  <td className="app-workflows-status-cell">
                    <span className={getStatusBadgeClass(workflow.status)}>
                      {workflow.status.replace('_', ' ')}
                    </span>
                  </td>
                  <td className="app-workflows-created-cell">
                    {formatDate(workflow.createdAt)}
                  </td>
                  <td className="app-workflows-updated-cell">
                    {formatDate(workflow.updatedAt)}
                  </td>
                  <td className="app-workflows-actions-cell">
                    <div className="app-workflows-actions-group">
                      <Icon
                        variant="tertiary"
                        size="md"
                        icon={<Eye size={16} />}
                        onClick={() => handleViewWorkflow(workflow)}
                        title="View Workflow"
                      />
                      {workflow.status === WorkflowStatus.CANCELLED ? (
                        <Icon
                          variant="tertiary"
                          size="md"
                          icon={<Play size={16} />}
                          onClick={() => handleResumeWorkflow(workflow)}
                          title="Resume Workflow"
                        />
                      ) : (
                        <Icon
                          variant="tertiary"
                          size="md"
                          icon={<Pause size={16} />}
                          onClick={() => handlePauseWorkflow(workflow)}
                          title="Pause Workflow"
                        />
                      )}
                      <Icon
                        variant="tertiary"
                        size="md"
                        icon={<Trash2 size={16} />}
                        onClick={() => handleDeleteWorkflow(workflow)}
                        title="Delete Workflow"
                      />
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* Workflow Modal */}
      {showWorkflowModal && selectedWorkflowId && (
        <div className="app-workflows-modal-overlay">
          <div className="app-workflows-modal">
            <div className="app-workflows-modal-header">
              <h2>Workflow Details</h2>
              <button
                className="app-workflows-modal-close"
                onClick={() => {
                  setShowWorkflowModal(false);
                  setSelectedWorkflowId(null);
                }}
              >
                <X size={20} />
              </button>
            </div>
            <div className="app-workflows-modal-content">
              <AppWorkflow
                dynamicContext={{ workflowId: selectedWorkflowId }}
                disabled={false}
              />
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {showDeleteConfirmationModal && pendingWorkflowId && (
        <ModalBasicConfirm
          isOpen={showDeleteConfirmationModal}
          title="Delete Workflow"
          message={
            (() => {
              const workflow = workflows.find(w => w.id === pendingWorkflowId);
              const workflowName = workflow?.vibeProcess?.name || 'this workflow';
              return `Are you sure you want to delete "${workflowName}"? This action cannot be undone.`;
            })()
          }
          continueLabel="Delete"
          onContinue={handleConfirmDelete}
          onClose={() => {
            setShowDeleteConfirmationModal(false);
            setPendingWorkflowId(null);
          }}
        />
      )}
    </div>
  );
};
